import {
  createAsyncThunk,
} from '@reduxjs/toolkit';

import type { ThunkApiType } from '@/store';
import type { PrivateBlogJson } from '@/types/domain/privateBlog/entities/privateBlogEntity';
import type { CategoryItemsType } from '@/types/domain/privateBlog/redux';

import { privateBlogSlice } from '@/domain/privateBlog/redux';
import { uiSlice } from '@/domain/ui/redux';
import { ApiResponseError } from '@/errors/apiResponseError';
import { service } from '@/share';

/**
 * カテゴリとページデータをリフレッシュする
 */
export const refreshCategoryItems = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
  },
  ThunkApiType
>(
  'privateBlog/refreshCategoryItems',
  async ({ nurseryId }, thunkApi) => {
    const result: CategoryItemsType = {};
    const catResult = await service.privateBlog.getCategoryList(nurseryId);
    for (let i = 0; i < catResult.items.length; i += 1) {
      const { categoryId } = catResult.items[i];
      const pageResult = await service.privateBlog.getPrivateBlogList(nurseryId, categoryId, null);
      result[categoryId] = {
        category: catResult.items[i],
        items: pageResult.items,
        nextToken: pageResult.nextToken,
      };
    }
    thunkApi.dispatch(privateBlogSlice.actions.setCategoryItems(result));
  },
);

/**
 * ページデータを取得
 */
export const getCategoryItems = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
    /** カテゴリID */
    categoryId: string,
  },
  ThunkApiType
>(
  'privateBlog/getCategoryItems',
  async ({ nurseryId, categoryId }, thunkApi) => {
    const { categoryItems } = thunkApi.getState().privateBlog;
    if (categoryItems && categoryItems[categoryId]) {
      const { nextToken } = categoryItems[categoryId];
      const pageResult = await service.privateBlog.getPrivateBlogList(
        nurseryId,
        categoryId,
        nextToken,
      );
      const result: CategoryItemsType = {};
      const catIdList = Object.keys(categoryItems);
      for (let i = 0; i < catIdList.length; i += 1) {
        const catId = catIdList[i];
        const categoryItem = categoryItems[catId];
        result[catId] = {
          category: { ...categoryItem.category },
          items: categoryItem.items.map((v) => ({ ...v })),
          nextToken: categoryItem.nextToken,
        };
        if (catId === categoryId) {
          pageResult.items.forEach((v) => {
            result[catId].items.push(v);
          });
          result[catId].nextToken = pageResult.nextToken;
        }
      }
      thunkApi.dispatch(privateBlogSlice.actions.setCategoryItems(result));
    }
  },
);

/**
 * コンテンツを取得
 */
export const loadContentPrivateBlog = createAsyncThunk<
  void,
  {
    /** データ */
    data: PrivateBlogJson | undefined,
    /** 施設ID */
    nurseryId: string,
  },
  ThunkApiType
>(
  'privateBlog/loadContentPrivateBlog',
  async ({ data, nurseryId }, thunkApi) => {
    if (!data) {
      thunkApi.dispatch(privateBlogSlice.actions.setPrivateBlogContent(undefined));
      return;
    }
    try {
      const content = await service.privateBlog.loadContentPrivateBlog(data, nurseryId);
      if (content) {
        thunkApi.dispatch(privateBlogSlice.actions.setPrivateBlogContent({
          item: content,
        }));
      }
    } catch (e) {
      if (e instanceof ApiResponseError) {
        thunkApi.dispatch(uiSlice.actions.setMessage({
          key: 'error',
          content: 'データの取得に失敗しました',
        }));
      } else {
        throw e;
      }
    }
  },
);
